import React, { useContext, useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import DeleteIcon from '@material-ui/icons/Delete';
import SearchIcon from '@material-ui/icons/Search';
import CircularProgress from '@material-ui/core/CircularProgress';
import { SuggestBox, ActionIcon } from '@mummssoftware/common-ui';
import { ErrorText } from '@mummssoftware/common-ui/style';
import { emptyOption } from '@mummssoftware/utils/constants';
import { getHCPCSdiagsearch } from '@mummssoftware/utils/hcpcsUtils';
import { HCPCSContext } from '../Context/contexts/HCPCSContext';
import {
  DELETE_ICD_CODE,
  ADD_ICD_CODE,
  RECORD_DATA,
} from '../Context/constants';
import { FlexGapDiv, ICDDescription, FullWidthDiv } from '../../components';
import { Context } from '../../context';
import { useStyles } from './style';
import { iconStyle } from './constants';
import defaults from '../../translations/en.json';

type ICDCodeProps = {
  hcpcsChoice: mumms.HCPCSMenuItem;
  index: number;
};

export const ICDCode = ({ hcpcsChoice, index }: ICDCodeProps) => {
  const { dispatchHCPCS, icdCodes, icdCodeNames } = useContext(HCPCSContext);
  const { keycloak, agency } = useContext(Context);
  const classes = useStyles();
  const intl = useIntl();

  const [selectValue, setCode] = useState<any>(emptyOption);
  const [icdCode, setIcdCode] = useState<string>('');
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [icdOptions, setIcdOptions] = useState<any>(null);
  const [noResults, setNoResults] = useState<boolean>(false);

  useEffect(() => {
    const savedIcdCodes = icdCodes && icdCodes[hcpcsChoice.key];
    const savedIcdCodeNames = icdCodeNames && icdCodeNames[hcpcsChoice.key];

    if (savedIcdCodes && savedIcdCodeNames && savedIcdCodeNames[index]) {
      const icdCodeValue = savedIcdCodeNames[index];

      if (savedIcdCodes[icdCodeValue]) {
        setCode({
          id: icdCodeValue,
          label: icdCodeValue,
          value: icdCodeValue,
          icd10code: icdCodeValue,
          icd10codeshortdescription: '',
        });
      }
    }
  }, [hcpcsChoice.key, index, icdCodes, icdCodeNames]);

  const onSelectChange = (target: any) => {
    if (target && target.id) {
      setCode(target);
      dispatchHCPCS({
        type: RECORD_DATA,
        payload: true,
      });
      dispatchHCPCS({
        type: ADD_ICD_CODE,
        payload: {
          key: hcpcsChoice.key,
          icdCode: target.id,
        },
      });
    } else {
      // click CANCEl
      setCode(emptyOption);
      setIcdOptions(null);
      setNoResults(false);
    }
  };

  const onDeleteICD = () => {
    setCode(emptyOption);
    dispatchHCPCS({
      type: RECORD_DATA,
      payload: true,
    });
    dispatchHCPCS({
      type: DELETE_ICD_CODE,
      payload: {
        key: hcpcsChoice.key,
        icdCode: selectValue.id,
        index,
      },
    });
  };

  const onTextChange = async (event: any) => {
    const targetValue = event.target.value;
    setIcdCode(targetValue.toUpperCase());
    if (noResults) {
      setNoResults(false);
    }
  };

  const onSearchCode = () => {
    setShowLoader(true);
    getICDcodes(icdCode);
  };

  const onKeyDown = (e: any) => {
    // on Enter
    if (e.keyCode === 13) {
      onSearchCode();
    }
  };

  const getLabel = (x: mumms.ICDcode) =>
    `${x.icd10code} - ${x.icd10codeshortdescription}`;

  const getDropdownOptions = (options: mumms.ICDcode[]) => {
    const dropdownOptions = options.map((x: mumms.ICDcode) => ({
      ...x,
      id: x.icd10code,
      label: getLabel(x),
      value: x.icd10code,
    }));
    return dropdownOptions;
  };

  const getICDcodes = async (text: string) => {
    const options = await getHCPCSdiagsearch(keycloak, agency, text);
    if (!(options instanceof Response) && !options.hint) {
      if (options.length) {
        const newOptions = getDropdownOptions(options);
        setIcdOptions(newOptions);
      }
      // if no results
      setShowLoader(false);
      setNoResults(true);
    }
    // if error fetching results
    setShowLoader(false);
    setNoResults(true);
  };

  return (
    <FlexGapDiv id="single-icd-code" hide={hcpcsChoice.hide}>
      {icdOptions ? (
        <>
          {selectValue.id ? (
            <>
              <ActionIcon
                style={{ ...iconStyle, marginLeft: '-10px' }}
                Icon={DeleteIcon}
                onClick={onDeleteICD}
              />
              <ICDDescription>{getLabel(selectValue)}</ICDDescription>
            </>
          ) : (
            <FullWidthDiv id="dropdown-icd">
              <SuggestBox
                fitcontent
                options={icdOptions}
                value={selectValue}
                onChange={onSelectChange}
                defaultMenuIsOpen={!selectValue.id}
                isClearable
                menuPlacement="top"
                noMaxHeight
              />
            </FullWidthDiv>
          )}
        </>
      ) : (
        <>
          <ActionIcon
            style={{ ...iconStyle, marginLeft: '-10px' }}
            Icon={DeleteIcon}
            onClick={onDeleteICD}
          />
          {selectValue.id ? (
            <ICDDescription>
              {selectValue.icd10codeshortdescription
                ? getLabel(selectValue)
                : selectValue.label || selectValue.id}
            </ICDDescription>
          ) : (
            <TextField
              id="icd-search"
              placeholder={defaults.smartSearch}
              value={icdCode}
              onChange={onTextChange}
              onKeyDown={onKeyDown}
              disabled={showLoader}
              InputProps={
                showLoader
                  ? {
                      endAdornment: (
                        <InputAdornment position="end">
                          <CircularProgress />
                        </InputAdornment>
                      ),
                      classes: { root: classes.textField },
                    }
                  : {
                      endAdornment: (
                        <InputAdornment position="end">
                          <ActionIcon
                            style={iconStyle}
                            Icon={SearchIcon}
                            onClick={onSearchCode}
                            disabled={!icdCode}
                          />
                        </InputAdornment>
                      ),
                      classes: { root: classes.textField },
                    }
              }
            />
          )}

          {noResults && (
            <ErrorText>
              {intl.formatMessage(defaults.noICDresults, {
                icdCode,
              })}
            </ErrorText>
          )}
        </>
      )}
    </FlexGapDiv>
  );
};
